import AsyncStorage from '@react-native-async-storage/async-storage';
// Use FileSystem for managing saved images
import * as FileSystem from 'expo-file-system';

const SAVED_POSTS_KEY = 'saved_posts_v1';
const DRAFTS_KEY = 'drafts_v1';

// Get document directory - handle different Expo versions
const getDocumentDirectory = (): string => {
  // For Expo 54+, documentDirectory is available at module level
  const fs = FileSystem as any;
  const docDir = fs.documentDirectory;
  if (docDir && typeof docDir === 'string') {
    return docDir;
  }
  // Fallback for different Expo versions
  return fs.cacheDirectory || '';
};

const SAVED_IMAGES_DIR = `${getDocumentDirectory()}saved_images/`;

export interface SavedPost {
  uri?: string;
  cid?: string;
  text?: string;
  author?: any;
  record?: any;
  createdAt?: string;
  localImages?: string[];
  [key: string]: any;
}

export interface DraftItem {
  id: string;
  text: string;
  createdAt: string;
}

export async function getSavedPosts(): Promise<SavedPost[]> {
  try {
    const raw = await AsyncStorage.getItem(SAVED_POSTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('getSavedPosts failed', e);
    return [];
  }
}

export async function savePost(post: SavedPost) {
  const current = await getSavedPosts();
  const exists = current.find(p => (p.uri && post.uri && p.uri === post.uri) || (p.cid && post.cid && p.cid === post.cid));
  if (!exists) {
    // Download images if any
    const localImages: string[] = [];
    const images = post.embed?.images || post.embed?.media?.images || post.images;
    
    if (images && Array.isArray(images)) {
      try {
        const dirInfo = await FileSystem.getInfoAsync(SAVED_IMAGES_DIR);
        if (!dirInfo.exists) {
          await FileSystem.makeDirectoryAsync(SAVED_IMAGES_DIR, { intermediates: true });
        }

        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          const imageUrl = typeof img === 'string' ? img : (img.fullsize || img.thumb || img.url);
          if (imageUrl) {
            const filename = `${post.cid || Date.now()}_${i}.jpg`;
            const localUri = `${SAVED_IMAGES_DIR}${filename}`;
            await FileSystem.downloadAsync(imageUrl, localUri);
            localImages.push(localUri);
          }
        }
      } catch (e) {
        console.error('Failed to download images', e);
      }
    }

    current.unshift({ ...post, localImages, savedAt: new Date().toISOString() });
    await AsyncStorage.setItem(SAVED_POSTS_KEY, JSON.stringify(current.slice(0, 200)));
  }
}

export async function removeSavedPost(key: string) {
  const current = await getSavedPosts();
  const postToRemove = current.find(p => p.uri === key || p.cid === key);
  
  if (postToRemove?.localImages) {
    for (const uri of postToRemove.localImages) {
      try {
        await FileSystem.deleteAsync(uri, { idempotent: true });
      } catch (e) {
        console.error('Failed to delete local image', e);
      }
    }
  }

  const filtered = current.filter(p => p.uri !== key && p.cid !== key);
  await AsyncStorage.setItem(SAVED_POSTS_KEY, JSON.stringify(filtered));
}

export async function getDrafts(): Promise<DraftItem[]> {
  try {
    const raw = await AsyncStorage.getItem(DRAFTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('getDrafts failed', e);
    return [];
  }
}

export async function addDraft(text: string): Promise<DraftItem> {
  const newDraft: DraftItem = { id: Date.now().toString(), text, createdAt: new Date().toISOString() };
  const drafts = await getDrafts();
  drafts.unshift(newDraft);
  await AsyncStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  return newDraft;
}

export async function removeDraft(id: string) {
  const drafts = await getDrafts();
  const filtered = drafts.filter(d => d.id !== id);
  await AsyncStorage.setItem(DRAFTS_KEY, JSON.stringify(filtered));
}

export async function updateDraft(id: string, text: string) {
  const drafts = await getDrafts();
  const updated = drafts.map(d => d.id === id ? { ...d, text } : d);
  await AsyncStorage.setItem(DRAFTS_KEY, JSON.stringify(updated));
}
